import { z } from "zod";
import { Area, Importance, Landing, Scope, Side, UsefulWindow, YouTubeClip, type Stuff, type StuffClip } from "./stuff.ts";

/**
 * Milestone 3, team play. Combos, executions and post-plants point at saved
 * singles instead of copying them: editing a single updates everything using it.
 */

const Title = z.string().trim().min(2, "At least 2 characters").max(60, "At most 60 characters");
const Notes = z.string().trim().max(1000).default("");
const Id = z.string().uuid();
const SlotNumber = z.number().int().min(1).max(5);

// ---------------------------------------------------------------- Combos

export const MAX_COMBO_STEPS = 8;
/** Throw spots farther apart than this (world units, about 5 m) make the form warn: a combo is thrown from one spot. */
export const COMBO_SPREAD_WARN = 160;

export const ComboStepInput = z.object({
  stuffId: Id,
  note: z.string().trim().max(140).default(""),
});

export const ComboInput = z
  .object({
    map: z.string(),
    side: Side,
    area: Area,
    title: Title,
    notes: Notes,
    steps: z.array(ComboStepInput).min(2, "A combo has at least 2 throws").max(MAX_COMBO_STEPS),
    windows: z.array(UsefulWindow).max(6).default([]),
    /** A video of the whole combo; without one (and no upload) the page shows each throw's. */
    youtube: YouTubeClip.nullable().default(null),
    importance: Importance.default("useful"),
  })
  .refine((c) => new Set(c.steps.map((s) => s.stuffId)).size === c.steps.length, {
    message: "The same throw is in the combo twice",
    path: ["steps"],
  });
export type ComboInput = z.infer<typeof ComboInput>;

export const CreateComboInput = z.intersection(ComboInput, z.object({ scope: Scope }));
export type CreateComboInput = z.infer<typeof CreateComboInput>;

export interface Combo {
  id: string;
  teamId: string | null;
  map: string;
  side: Side;
  area: Area;
  title: string;
  notes: string;
  steps: { note: string; stuff: Stuff }[];
  windows: UsefulWindow[];
  youtube: YouTubeClip | null;
  importance: Importance;
  /** Its own uploaded video of the whole combo, if any. */
  clip: StuffClip | null;
  author: { id: string; name: string };
  canEdit: boolean;
  /** Steps whose single I have learned. */
  learnedByMe: number;
  createdAt: string;
  updatedAt: string;
}

/** Largest distance between two throw spots of a combo, in world units (height ignored). */
export function comboSpread(spots: { x: number; y: number }[]): number {
  let max = 0;
  for (let i = 0; i < spots.length; i++)
    for (let j = i + 1; j < spots.length; j++) max = Math.max(max, Math.hypot(spots[i].x - spots[j].x, spots[i].y - spots[j].y));
  return max;
}

// ---------------------------------------------------------------- Executions

export const ROLES = ["entry", "trade", "support", "smokes", "lurk", "awp"] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

// ---------------------------------------------------------------- Post-plant

export const SITES = ["a", "b"] as const;
export const Site = z.enum(SITES);
export type Site = z.infer<typeof Site>;

/**
 * What a plant spot plans: the T side holding after planting there, or the CT
 * side taking the site back with the bomb down there. Both run on the bomb timer.
 */
export const PLANT_KINDS = ["postplant", "retake"] as const;
export const PlantKind = z.enum(PLANT_KINDS);
export type PlantKind = z.infer<typeof PlantKind>;
/** The side that plays each kind. */
export const plantKindSide = (k: PlantKind): Side => (k === "retake" ? "ct" : "t");

/** The bomb timer: 40 seconds from the plant. */
export const BOMB_SECONDS = 40;

export const PlantUtilityInput = z
  .object({
    stuffId: Id,
    slot: SlotNumber,
    /** Bomb timer, counting down: thrown between `from` and `to` seconds left. */
    from: z.number().int().min(0).max(BOMB_SECONDS),
    to: z.number().int().min(0).max(BOMB_SECONDS),
  })
  .refine((u) => u.from >= u.to, { message: "The bomb timer counts down: From is the larger number" });
export type PlantUtilityInput = z.infer<typeof PlantUtilityInput>;

export const PlantPositionInput = z.object({
  slot: SlotNumber,
  label: z.string().trim().max(40).default(""),
  x: z.number().finite().min(-16384).max(16384),
  y: z.number().finite().min(-16384).max(16384),
  z: z.number().finite().min(-16384).max(16384).nullable().default(null),
  /** Where the player looks, in CS2 yaw degrees (0 = east, 90 = north). */
  yaw: z.number().finite().min(-360).max(360),
  /** How far that is, in world units: the view line ends there. Null: a default length. */
  reach: z.number().finite().min(0).max(8000).nullable().default(null),
  /** The teammate who plays this spot: they get its player's utility tracked, as in executions. */
  memberId: Id.nullable().default(null),
});
export type PlantPositionInput = z.infer<typeof PlantPositionInput>;

export const PlantSpotInput = z.object({
  map: z.string(),
  /** Retakes are executions since 0.8.0: a plant spot is a post-plant. */
  kind: z.literal("postplant").default("postplant"),
  site: Site,
  /** "Default", "Open", "Safe". */
  name: z.string().trim().min(1, "Give the plant spot a name").max(30),
  plant: Landing,
  notes: Notes,
  utility: z.array(PlantUtilityInput).max(10).default([]),
  positions: z
    .array(PlantPositionInput)
    .max(5)
    .default([])
    .refine(
      (ps) => {
        const pinned = ps.map((p) => p.memberId).filter(Boolean);
        return new Set(pinned).size === pinned.length;
      },
      { message: "A teammate is pinned to two positions" },
    ),
});
export type PlantSpotInput = z.infer<typeof PlantSpotInput>;

/** How long a view line is when no length was set, in world units (about 13 m). */
export const DEFAULT_REACH = 420;

/** Plant spot screenshots: where to stand, where to aim, the bomb down, anything else. */
export const PLANT_CAPTIONS = ["stand", "aim", "planted", "other"] as const;
export const PlantCaption = z.enum(PLANT_CAPTIONS);
export type PlantCaption = z.infer<typeof PlantCaption>;
export const MAX_PLANT_PICTURES = 4;

export interface PlantMedia {
  id: string;
  url: string;
  caption: PlantCaption;
}

export const CreatePlantSpotInput = z.intersection(PlantSpotInput, z.object({ scope: Scope }));
export type CreatePlantSpotInput = z.infer<typeof CreatePlantSpotInput>;

export interface PlantSpot {
  id: string;
  teamId: string | null;
  map: string;
  kind: PlantKind;
  site: Site;
  name: string;
  plant: Landing;
  notes: string;
  media: PlantMedia[];
  utility: (Omit<PlantUtilityInput, "stuffId"> & { stuff: Stuff })[];
  positions: (PlantPositionInput & { member: { id: string; name: string; avatarUrl: string | null } | null })[];
  author: { id: string; name: string };
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What uses a single: shown on its page, and before editing it. */
export interface UsedIn {
  kind: "combo" | "execution" | "plant";
  id: string;
  title: string;
}
